// UC92 — expiring lots verification for observers + system admin.
import { useQuery } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { MudSegmentedControl, MudTable, MudTag } from '@egov-moldova/mud-react';
import { EMPTY_CELL, EmptyState, formatExpiry, formatNumber } from '@simsm/ui-shared';
import { apiClient } from '../../app/api-client';
import { useCriteriaParams } from '../../app/use-criteria-params';
import { QueryState } from '../../components/QueryState/QueryState';
import { ObserverScopeBanner } from '../../components/AppShell/ObserverScopeBanner';

type WindowDays = 30 | 60 | 90 | 180;

const WINDOWS: readonly WindowDays[] = [30, 60, 90, 180];
const DEFAULT_WINDOW: WindowDays = 90;

// ACCS-13: the window is a search criterion, so it lives in the address and a
// reload or a bookmark reopens the same list. A value the screen does not offer
// falls back to the default. per docs/shape-ops-readable-urls-and-history.md §2
function readWindow(value: string | null): WindowDays {
  return WINDOWS.find((option) => String(option) === value) ?? DEFAULT_WINDOW;
}

interface ExpiringRow {
  medicineCode: string;
  medicineLabel: string;
  lot: string;
  lotExpiry: string;
  // 'DAY' | 'MONTH': how the lot's expiry was reported; it decides how the date is shown.
  lotExpiryPrecision?: string;
  daysRemaining: number;
  quantity: number;
  registrarId: string;
  siteCode: string;
  siteName: string;
}

async function fetchExpiring(days: WindowDays): Promise<ExpiringRow[]> {
  const { data } = await apiClient.get<{ entries: ExpiringRow[] }>(`/stock/expiring?days=${days}`);
  return data.entries ?? [];
}

function urgencyForDays(days: number): {
  level: 'critical' | 'high' | 'medium' | 'low';
  semantic: 'danger' | 'accent' | 'success';
} {
  if (days < 0) return { level: 'critical', semantic: 'danger' };
  if (days <= 30) return { level: 'critical', semantic: 'danger' };
  if (days <= 60) return { level: 'high', semantic: 'accent' };
  if (days <= 90) return { level: 'medium', semantic: 'accent' };
  return { level: 'low', semantic: 'success' };
}

function rowId(row: ExpiringRow): string {
  return `${row.medicineCode}-${row.lot}-${row.siteCode}`;
}

export function ExpiringLotsPage(): JSX.Element {
  const { t } = useTranslate();
  const [params, setParams] = useCriteriaParams();
  const days = readWindow(params.get('days'));
  function setDays(value: WindowDays): void {
    const next = new URLSearchParams(params);
    if (value === DEFAULT_WINDOW) next.delete('days');
    else next.set('days', String(value));
    setParams(next);
  }
  const query = useQuery({
    queryKey: ['stock-expiring', days],
    queryFn: () => fetchExpiring(days),
  });
  const rows = query.data ?? [];
  const windows = WINDOWS;

  const columns = [
    { key: 'medicine', label: t('stock_check.column.medicine') },
    { key: 'lot', label: t('stock_check.column.lot') },
    { key: 'expiry', label: t('stock_check.column.expiry') },
    { key: 'days', label: t('stock_check.column.days') },
    { key: 'quantity', label: t('stock_check.column.quantity'), align: 'end' as const },
    { key: 'site', label: t('stock_check.column.site') },
  ];

  function daysLabel(remaining: number): string {
    if (remaining < 0) return t('stock_check.days.expired', { days: Math.abs(remaining) });
    if (remaining === 0) return t('stock_check.days.today');
    return String(remaining);
  }

  const tableRows = rows.map((r) => ({
    id: rowId(r),
    medicine: r.medicineLabel || r.medicineCode,
    lot: r.lot,
    expiry: formatExpiry(r.lotExpiry, r.lotExpiryPrecision),
    days: daysLabel(r.daysRemaining),
    quantity: formatNumber(r.quantity),
    site: r.siteName || r.siteCode || EMPTY_CELL,
  }));

  return (
    <section className="simsm-stock-check">
      <header className="simsm-stock-check__header">
        <h1 className="simsm-stock-check__title">{t('stock_check.title')}</h1>
        <p className="simsm-stock-check__lede">{t('stock_check.lede')}</p>
      </header>

      <ObserverScopeBanner />

      <div className="simsm-stock-check__windows">
        <MudSegmentedControl
          size="md"
          value={String(days)}
          aria-label={t('stock_check.window.aria_label')}
          segments={windows.map((w) => ({ value: String(w), label: t(`stock_check.window.${w}`) }))}
          onMudChange={(e: CustomEvent<{ value: string }>) =>
            setDays(Number(e.detail.value) as WindowDays)
          }
        />
      </div>

      <QueryState
        query={query}
        isEmpty={rows.length === 0}
        empty={
          /* Nothing expiring is good news, and the wireframe carries good news
             with the GLYPH, never with a surface: Nefinalizate.tsx:65 gives its
             "no unfinished report" screen the ordinary `.empty` card
             (mud.css:1862 — text-tertiary, the icon in --border-secondary) and
             changes only Icon.doc to Icon.check. The green notification this
             replaces was the second success-variant empty in the tree, three
             days after spec 009 removed the first.
             Both lines name the window, because the window is a query parameter
             (`days` is in the query key, so the answer is re-fetched) and the
             sentence is only true of the window it was fetched for. */
          <EmptyState
            icon="checkmark-large"
            title={t('stock_check.empty.title', { days })}
            body={t('stock_check.empty.body', { days })}
          />
        }
      >
        {/* Six columns, the MUD table's published ceiling; a shell narrower than
            the table scrolls it inside the table's own wrapper (docs/ui-shape.md). */}
        <MudTable
          className="simsm-table"
          columns={columns}
          rows={tableRows}
          rowStyle="divided"
          hoverable={true}
          disableSort={true}
          aria-label={t('stock_check.table.aria_label')}
        >
          {rows.flatMap((r, index) => {
            const id = rowId(r);
            const urgency = urgencyForDays(r.daysRemaining);
            return [
              // The label is the medicine; the code stays beneath it as the
              // identity the stock row was filed under (audit E1).
              <span
                key={`medicine-${id}`}
                slot={`cell-medicine-${index}`}
                className="simsm-stock-check__cell-medicine"
              >
                <strong>{r.medicineLabel || r.medicineCode}</strong>
                <code>{r.medicineCode}</code>
              </span>,
              <code key={`lot-${id}`} slot={`cell-lot-${index}`}>
                {r.lot}
              </code>,
              <span
                key={`days-${id}`}
                slot={`cell-days-${index}`}
                className="simsm-stock-check__cell-days"
              >
                <MudTag
                  variant="status"
                  semantic={urgency.semantic}
                  label={t(`stock_check.urgency.${urgency.level}`)}
                />
                <span>{daysLabel(r.daysRemaining)}</span>
              </span>,
              <span
                key={`quantity-${id}`}
                slot={`cell-quantity-${index}`}
                className="simsm-stock-check__cell-quantity"
              >
                {formatNumber(r.quantity)}
              </span>,
              // The column is headed "Locație", so it names the location, with
              // the internal code beneath it.
              <span
                key={`site-${id}`}
                slot={`cell-site-${index}`}
                className="simsm-stock-check__cell-site"
              >
                <span className="simsm-stock-check__site-name">
                  {r.siteName || r.siteCode || EMPTY_CELL}
                </span>
                {r.siteName && r.siteCode ? <code>{r.siteCode}</code> : null}
              </span>,
            ];
          })}
        </MudTable>
      </QueryState>
    </section>
  );
}
